import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import debounce from 'debounce';
import { CalendarDate } from "@internationalized/date";
import { format, formatISO, eachMinuteOfInterval, set, addMinutes, subMinutes } from 'date-fns'
import { fr } from 'date-fns/locale'
import { Parser } from "expr-eval"
import { Turbo } from "@hotwired/turbo-rails"

export function cn(...inputs) {
	return twMerge(clsx(inputs));
}

export function titleCase(string) {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}

export function formatPrice(cents) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(cents / 100)
}

// eg: 2025-06-06
export function formatDateISO(date) {
  if (date === "") return ""
  return formatISO(new Date(date), { representation: 'date' })
}

// eg: 25/12/2024
export function formatDate(date) {
  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "short" }).format(new Date(date))
}

// eg: 25/12/2024 10:00
export function formatDateTime(date) {
  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "short", timeStyle: "short" }).format(new Date(date))
}

export function formatDateText(date) {
  return new Intl.DateTimeFormat("fr-FR", { dateStyle: "long" }).format(new Date(date))
}

export function ago(date) {
  return new Intl.RelativeTimeFormat("fr-FR").format(-Math.round((new Date() - new Date(date)) / (1000 * 60 * 60 * 24)), "day")
}

export function removeEmptyValues(obj) {
  return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== ""))
}

export function clearObjectValues(obj) {
  return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, ""]))
}

export function paginationSummary(pagy) {
  if (pagy.count === 0) return "Aucun élément correspondant";
  if (pagy.count === 1) return "1 élément correspondant";
  const count = new Intl.NumberFormat("fr-FR").format(pagy.count)
  return `Affichage ${pagy.from}-${pagy.to} de ${count} éléments correspondants`;
}

// Create a function that takes two objects and returns the difference between them.
// It should return an array of onjects with each object having a key value and a changes array of [old, new]
export function diffObjects(obj1, obj2) {
  return Object.keys(obj1)
    .filter(key => {
      const val1 = obj1[key] ?? ""
      const val2 = obj2[key] ?? ""
      return val1 !== val2
    })
    .map(key => ({
      key,
      value: obj1[key],
      changes: [obj1[key], obj2[key]]
    }))
}

export function isEmpty(object) {
  return Object.keys(object).length === 0 && object.constructor === Object
}

export function getAddressFromGooglePlace(place) {
  const houseNumber = place.addressComponents.find(component => component.types.includes("street_number"))?.longText ?? ""
  const street = place.addressComponents.find(component => component.types.includes("route"))?.longText ?? ""
  const street1 = [houseNumber, street].filter(Boolean).join(" ")
  // Town can be locality or postal_town
  const town = place.addressComponents.find(component => component.types.includes("locality") || component.types.includes("postal_town"))?.longText ?? ""
  const postcode = place.addressComponents.find(component => component.types.includes("postal_code"))?.longText ?? ""
  const countryName = place.addressComponents.find(component => component.types.includes("country"))?.longText ?? ""
  const countryCode = place.addressComponents.find(component => component.types.includes("country"))?.shortText ?? ""
  return { text: place.formattedAddress, houseNumber, street, street1, town, postcode, country: { name: countryName, code: countryCode } }
}

export function createDebouncedPromise(fn) {
  return new Promise((resolve) => {
    const debouncedFn = debounce(async () => {
      try {
        await fn()
      } finally {
        resolve()
      }
    }, 450)
    debouncedFn()
  })
}

export function dateString(date) {
  if (!(date instanceof CalendarDate)) return ""
  const day = date.day.toString().padStart(2, '0')
  const month = date.month.toString().padStart(2, '0')
  return `${day}/${month}/${date.year}`
}

export function dateRangeString({ start, end }) {
  if (!start || !end) return ""
  // If start and end are the same day, return the date eg: 24 mai 2025
  // If start and end are the same month, don't repeat the month and year eg: 24-28 mai 2025
  // If start and end are the same year, don't repeat the year eg: 24 mai - 28 juin 2025
  const isSameDay = start.day === end.day && start.month === end.month && start.year === end.year
  const isSameMonth = start.month === end.month && start.year === end.year
  const isSameYear = start.year === end.year

  const startMonthName = new Intl.DateTimeFormat("fr-FR", { month: "long" }).format(start.toDate())
  const endMonthName = new Intl.DateTimeFormat("fr-FR", { month: "long" }).format(end.toDate())

  // 8 mai 2025
  if (isSameDay) return formatDateText(start)
  
  // 1 - 31 mai 2025
  if (isSameMonth) return `${start.day}-${end.day} ${startMonthName} ${start.year}`
  
  // 1 mai - 31 juin 2025
  if (isSameYear) return `${start.day} ${startMonthName} - ${end.day} ${endMonthName} ${start.year}`

  // 1 mai - 31 juin 2025
  return `${start.day} ${formatDateText(start)} - ${end.day} ${formatDateText(end)}`
}

export function arrayMatch(array1, array2) {
  return JSON.stringify(array1) === JSON.stringify(array2)
}

export function formatAge(dob, { showMonths = false, calculatedAt = new Date() } = {}) {
  if (!dob) return null
  
  const dobDate = new Date(dob)

  const ageInDays = Math.floor((calculatedAt - dobDate) / (1000 * 60 * 60 * 24))
  const ageInMonths = (calculatedAt.getFullYear() * 12 + calculatedAt.getMonth()) - (dobDate.getFullYear() * 12 + dobDate.getMonth())
  let ageInYears = calculatedAt.getFullYear() - dobDate.getFullYear()
  
  // Adjust years if birthday hasn't occurred yet this year
  if (dobDate.getMonth() > calculatedAt.getMonth() || 
      (dobDate.getMonth() === calculatedAt.getMonth() && dobDate.getDate() > calculatedAt.getDate())) {
    ageInYears--
  }

  if (ageInDays < 30) {
    return ageInDays === 1 ? "1 jour" : `${ageInDays} jours`
  } else if (ageInMonths < 12) {
    return `${ageInMonths} mois`
  } else {
    let ageString = ageInYears === 1 ? "1 an" : `${ageInYears} ans`
    const leftoverMonths = ageInMonths - ageInYears * 12
    if (leftoverMonths > 0 && showMonths) {
      ageString += ` et ${leftoverMonths} mois`
    }
    return ageString
  }
}

export function delay(ms = 100) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export function mergePath(path, params = {}, { replace = false } = {}) {
  path ||= window.location
  const url = new URL(path)

  if (replace) {
    url.search = "" // wipe existing params
  }

  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === "") {
      url.searchParams.delete(key) // remove param
    } else {
      url.searchParams.set(key, value) // add/update param
    }
  })

  return url.toString()
}

export function getActiveParams() {
  const url = new URL(window.location.href)
  return Object.fromEntries(Array.from(url.searchParams.entries()).filter(([key]) => key !== "page"))
}

export function removeParam(param, options = {}) {
  Turbo.visit(mergePath(null, { [param]: null }), options)
}

export function removeParams(keys = [], options = {}) {
  const params = Object.fromEntries(keys.map(key => [key, null]))
  Turbo.visit(mergePath(null, params), options)
}

export function setParam(key, value, options = {}) {
  Turbo.visit(mergePath(null, { [key]: value }), options)
}

export function setParams(params = {}, options = {}) {
  Turbo.visit(mergePath(null, params), options)
}

export function pathToCSV() {
  return window.location.pathname + '.csv' + window.location.search
}

export function exportCSV() {
  window.open(pathToCSV(), '_blank')
}

export function calendarTitle(dates = [new Date()]) {
  if (!dates || dates.length === 0) return "Calendrier"
  
  // First ensure dates are instances of Date
  dates = dates.map(date => new Date(date))
  
  // Dates are the same - "mardi 16 aout 2023"
  if (dates.length === 1) return format(dates[0], "EEEE dd MMMM yyyy", { locale: fr })
  
  // Dates are in the same month and year - "16-17 aout 2023"
  if (dates.every(date => date.getMonth() === dates[0].getMonth() && date.getFullYear() === dates[0].getFullYear())) {
    
    let startNumber = dates[0].getDate()
    let endNumber = dates[dates.length - 1].getDate()
    let monthAndYear = format(dates[0], "MMMM yyyy", { locale: fr })
    return `${startNumber}-${endNumber} ${monthAndYear}`
  }
  
  // Dates are in the same year - "31 aout - 1 septembre 2023" 
  if (dates.every(date => date.getFullYear() === dates[0].getFullYear())) {

  }
  
  // Dates are in different years - "31 décembre 2023 - 1 janvier 2024"
  let startDate = format(dates[0], "dd MMMM yyyy", { locale: fr })
  let endDate = format(dates[dates.length - 1], "dd MMMM yyyy", { locale: fr })
  return `${startDate} - ${endDate}`
}

export function getTimeSlots(date, { duration = 15, spaced = true } = {}) {
  const start = set(date, { hours: 8, minutes: 0, seconds: 0, milliseconds: 0 })
  const end = subMinutes(set(date, { hours: 21, minutes: 0, seconds: 0, milliseconds: 0 }), duration)
  
  return eachMinuteOfInterval({ start, end }, { step: duration }).map(slot => {
    const slotStart = subMinutes(slot, duration)
    const slotEnd = addMinutes(slot, duration)
    const formattedTimes = [format(slot, "HH:mm"), format(slotEnd, "HH:mm")]
    const formattedSlot = spaced ? formattedTimes.join(" - ") : formattedTimes.join("-")
    return { 
      start: slot, 
      end: slotEnd,
      formatted: { start: formattedTimes[0], end: formattedTimes[1], slot: formattedSlot } 
    }
  })
}

export function isEven(number) {
  return number % 2 === 0
}

export function isOdd(number) {
  return number % 2 !== 0
}

// selection: Array<{ start: Date|string|number, end: Date|string|number }>
// slot:      { start: Date|string|number, end: Date|string|number }
export function slotWithinSelection(slot, selection, opts = {}) {
  if (!Array.isArray(selection) || selection.length === 0) return false;

  const { inclusiveStart = true, inclusiveEnd = true } = opts;

  const toMs = (d) => (d instanceof Date ? d.getTime() : new Date(d).getTime());

  // Get overall bounds of the selection
  let minStart = Infinity;
  let maxEnd = -Infinity;

  for (const s of selection) {
    const sStart = toMs(s.start);
    const sEnd = toMs(s.end);
    if (Number.isNaN(sStart) || Number.isNaN(sEnd)) return false; // bad data
    if (sStart < minStart) minStart = sStart;
    if (sEnd > maxEnd) maxEnd = sEnd;
  }

  // Convert the slot we’re testing
  const slotStart = toMs(slot.start);
  const slotEnd = toMs(slot.end);
  if (Number.isNaN(slotStart) || Number.isNaN(slotEnd)) return false;

  // Decide boundary operators
  const leftOK  = inclusiveStart ? slotStart >= minStart : slotStart > minStart;
  const rightOK = inclusiveEnd   ? slotEnd   <= maxEnd   : slotEnd   < maxEnd;

  // Also ensure the slot isn’t inverted
  const slotOK = slotStart <= slotEnd;

  return leftOK && rightOK && slotOK;
}


export function roundToInterval(value, interval) {
  return Math.round(value / interval) * interval
}

export function initials(name) {
  return name.split(" ").map(n => n[0]).join("").toUpperCase()
}

export function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export function sortByStartTime(items, { startKey = "start_time", endKey = "end_time", map = true } = {}) {

  if (map) {
    items = items.map(item => ({
      ...item,
      [startKey]: item[startKey] ? new Date(item[startKey]) : null,
      [endKey]: item[endKey] ? new Date(item[endKey]) : null
    }))
  }

  return items.sort((a, b) => {
    if (!a[startKey] && !b[startKey]) return 0
    if (!a[startKey]) return -1
    if (!b[startKey]) return 1
    return a[startKey].getTime() - b[startKey].getTime()
  })
}

export function calculateFromQuery(query) {
  if (!query) return null
  
  if (query.startsWith('+') || query.startsWith('-') || query.startsWith('*') || query.startsWith('/')) {
    return null
  }

  if (!/(\d|\))\s*[+\-*/^]\s*(\d|\()/.test(query)) {
    return null
  }

  const parser = new Parser({
    // We don't want to allow assignment, logical or comparison operators, (no variables allowed)
    operators: {
      assignment: false,
      logical: false,
      comparison: false
    }
  })

  try {
    return parser.evaluate(query)
  } catch (error) {
    return null
  }
  
}

// ===== Functions from app/javascript/utils.js =====

export const datepickerText = {
  days: ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"],
  daysShort: ["dim.", "lun.", "mar.", "mer.", "jeu.", "ven.", "sam."],
  daysMin: ["d", "l", "ma", "me", "j", "v", "s"],
  months: ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"],
  monthsShort: ["janv.", "févr.", "mars", "avril", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."],
  today: "Aujourd'hui",
  monthsTitle: "Mois",
  clear: "Effacer",
  weekStart: 1,
  format: "dd/mm/yyyy"
}

export const datepickerOptions = {
  format: 'dd/mm/yyyy',
  language: 'fr'
}

export function getUserId() {
  const json = document.body.dataset.user
  return JSON.parse(json).id
}

export function setFloatValue(value) {
  if (!value) return 0
  return parseFloat(parseFloat(value).toFixed(2))
}

export function removeEmptyFormValues(form) {
  const formData = new FormData(form)

  for (const [key, value] of formData.entries()) {
    if (value) continue
    const input = form.querySelector(`[name="${key}"]`)
    if (input) input.disabled = true
  }
}

// Takes a date object (or string) and returns a formatted date string YYYY-MM-DD that allows for timezone offset
export function formattedDate(date) {
  date = new Date(date)
  const offset = date.getTimezoneOffset(date)
  const adaptedDate = new Date(date.getTime() - (offset * 60 * 1000))
  return adaptedDate.toISOString().slice(0, 10)
}

export function closeModal(controllerInstance) {
  // Get the closest data-controller="modal" element
  const modalElement = controllerInstance.element.closest("[data-controller='modal']")

  // Get the stimulus controller instance for the modal element
  const modalController = controllerInstance.application.getControllerForElementAndIdentifier(modalElement, "modal")

  // Call the hide method on the modal controller if found
  modalController && modalController.hide()
}

export function showAlert(message, { type = "info" } = {}) {
  // Determine the correct icon based on the type
  let iconClass = "";
  if (type === "success") {
    iconClass = "fal fa-check-circle text-green-600 text-sm";
  } else if (["error", "warning"].includes(type)) {
    iconClass = "fal fa-exclamation-triangle text-red-800 text-sm";
  } else {
    iconClass = "fal fa-info-circle text-gray-600 text-sm";
  }

  const target = document.getElementById("flash-container")

  // Create the flash message HTML using template literals
  const alertMessage = `
    <div class="flash-message ${type}"
      data-controller="notification"
      data-notification-active-class="opacity-100 translate-y-0"
      data-turbo-temporary="false">
    <i class="${iconClass}"></i>
    <p class="text-smaller">${message}</p>
    <button class="ml-auto" data-action="notification#close">
      <i class="fal fa-times text-gray-600"></i>
    </button>
  </div>
  `;

  // Append the flash message to the target element
  target.insertAdjacentHTML("beforeend", alertMessage);
}

export function hasTextSelection(element) {
  const selection = window.getSelection()

  // Check if there's any selection and if it's within our element
  if (selection.rangeCount > 0 && element.contains(selection.getRangeAt(0).commonAncestorContainer)) {
    return selection.toString().length > 0
  }

  return false
}

export function createModal({ content = "", title = "", width = "50rem" }) {
  return `
    <div class="modal-bg"
      data-action="click@window->modal#handleBackgroundClick turbo:submit-end@window->modal#handleSubmitEnd"
      data-controller="modal"
      data-modal-autofocus-value="true"
      data-modal-close-on-escape-value="true"
      data-modal-close-on-form-complete-value="false"
      style="align-items: flex-start"
    >
      <section id="modal" class="modal active" data-controller="modal" style="width: ${width}">
        <header class="modal-header">
          <h2 class="modal-title">${title}</h2>
        </header>
        <main class="p-10">
          <div>${content}</div>
        </main>
      </section>
    </div>
  `
}

export function closeModalAndVisitPath(context, path) {
  const modalParent = context.element.closest("[data-controller=modal]")
  if (modalParent) {
    const modalController = context.application.getControllerForElementAndIdentifier(modalParent, "modal")
    modalController?.hide()
  }
  setTimeout(() => {
    Turbo.visit(path)
  }, 50)
}

export function getSafeParams(params = [], { searchString = window.location.search } = {}) {
  const urlSearchParams = new URLSearchParams(searchString)
  const result = {}
  for (const key of params) {
    const value = urlSearchParams.get(key);
    if (value !== null) {
      result[key] = value;
    }
  }
  return result
}

export function getSafeParam(param, { searchString = window.location.search, transform } = {}) {
  const urlSearchParams = new URLSearchParams(searchString)
  const value = param.includes("[]") ? urlSearchParams.getAll(param) : urlSearchParams.get(param);
  if (transform) {
    // Pass tranform to each value in the array
    return value.map(transform);
  }
  return value;
}

export function incrementNumber(string, step = 0.01, decimals = 2) {
  const num = parseFloat(string);
  if (isNaN(num)) return string;
  return (num + step).toFixed(decimals);
}

export function decrementNumber(string, step = 0.01, decimals = 2) {
  const num = parseFloat(string);
  if (isNaN(num)) return string;
  return (num - step).toFixed(decimals);
}

export function getCompanyType(string) {
  if (!string) return "";
  if (string.includes("SARL")) return "SARL";
  if (string.includes("SERL")) return "SERL";
  if (string.includes("SAS")) return "SAS";
  if (string.includes("SASU")) return "SASU";
  if (string.includes("SA")) return "SA";
  if (string.includes("SNC")) return "SNC";
  if (string.includes("SCOP")) return "SCOP";
  if (string.includes("SCA")) return "SCA";
  if (string.includes("EURL")) return "EURL";
  if (string.includes("SCI")) return "SCI";
  if (string.includes("Entrepreneur individuel")) return "Entrepreneur";
  return "";
}

export function capitalize(string) {
  const lowerCased = string.toLowerCase()
  return lowerCased.charAt(0).toUpperCase() + lowerCased.slice(1)
}

export const organisationTypes = [
  "SARL",
  "SERL",
  "SAS",
  "SASU",
  "SA",
  "SNC",
  "SCOP",
  "SCA",
  "EURL",
  "SCI",
  "Entrepreneur"
]

export const commonFrenchFirstNames = [
  "jean", "henri", "marie", "claire", "paul", "jacques", "louis", "andre", "francois", "george", "emmanuel", "eric", "philippe", "bernard", "christophe", "david", "thomas", "sophie", "julie", "isabelle", "anne", "catherine"
]

export function formatMoney(amount) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
}

export function currentUser() {
  return document?.body?.dataset?.user ? JSON.parse(document.body.dataset.user) : null
}

export function currentClinic() {
  return document?.body?.dataset?.clinic ? JSON.parse(document.body.dataset.clinic) : null
}

export function getClinics() {
  return document.body.dataset.clinics ? JSON.parse(document.body.dataset.clinics) : []
}

export function getVets() {
  return document.body.dataset.vets ? JSON.parse(document.body.dataset.vets) : []
}

export function isPreviewMode() {
  return document.documentElement.hasAttribute("data-turbo-preview")
}
